import { Building2 } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';

interface Organization {
  id: string;
  attributes: {
    name: string;
    admin_names?: string;
    total_license_users?: number;
    total_products?: number | string;
  };
}

const columns: ColumnDef<Organization>[] = [
  {
    key: 'account',
    header: 'Account',
    render: (item) => item.attributes.name,
  },
  {
    key: 'admin_names',
    header: 'Admin(s)',
    render: (item) => item.attributes.admin_names || 'None Assigned',
  },
  {
    key: 'total_license_users',
    header: 'License Users',
    render: (item) => item.attributes.total_license_users || 0,
  },
  {
    key: 'total_products',
    header: '# Tools',
    render: (item) => item.attributes.total_products || 'N/A',
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'product_id', type: 'product-select', label: 'Product', placeholder: 'All Products' },
    {
      key: 'org_type',
      type: 'select',
      label: 'Organization Type',
      placeholder: 'All Organizations',
      options: [
        { value: 'licensed', label: 'With License Admins' },
        { value: 'unlicensed', label: 'Without License Admins' },
      ],
    },
    { key: 'affiliate_id', type: 'affiliate-select', label: 'Affiliate', placeholder: 'All Affiliates', showFor: ['super', 'admin', 'affiliate_admin'] },
    { key: 'license_admin_id', type: 'organization-select', label: 'License Holder', placeholder: 'All License Holders', showFor: ['super', 'admin', 'affiliate_admin'] },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(
      filters.product_id ||
      filters.org_type ||
      filters.affiliate_id ||
      filters.license_admin_id
    ),
};

export function OrganizationListReport() {
  return (
    <ReportPageLayout<Organization>
      title="Organization List Report"
      description="View organizations and their license administrators."
      breadcrumbLabel="Organization List"
      itemLabel="organizations"
      queryKey="organization-list"
      fetchData={reportsService.getOrganizationList}
      downloadData={reportsService.downloadOrganizationList}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort((a, b) => {
          const nameA = (a.attributes?.name || '').toLowerCase();
          const nameB = (b.attributes?.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        })
      }
      getRowKey={(item) => item.id}
      emptyIcon={<Building2 />}
      emptyTitle="No organizations found"
      emptyDescription="Try adjusting your filters to find organizations."
    />
  );
}
